import React from 'react';
import { Link } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import './PrivacyPolicyPage.css';

const PrivacyPolicyPage: React.FC = () => {
  return (
    <main className="privacy-policy">
      <div className="privacy-policy__hero">
        <div className="container">
          <Link to="/" className="privacy-policy__back">
            <FiArrowLeft /> Back to Home
          </Link>
          <h1 className="privacy-policy__title">Privacy Policy</h1>
          <p className="privacy-policy__subtitle">
            Last Updated: May 23, 2026
          </p>
        </div>
      </div>

      <div className="container privacy-policy__body">
        <article className="privacy-policy__content">
          <section>
            <h2>Introduction</h2>
            <p>
              CodeTunez Studios LLC ("we," "our," or "us") is committed to protecting your privacy. 
              This Privacy Policy explains how we collect, use, disclose, and safeguard your information 
              when you use our applications and services (collectively, the "Services").
            </p>
            <p>
              Please read this privacy policy carefully. If you do not agree with the terms of this 
              privacy policy, please do not access or use our Services.
            </p>
          </section>

          <section>
            <h2>Apps That Do Not Collect Data</h2>
            <p>
              Some of our applications are designed to operate entirely locally on your device and 
              <strong> do not collect, transmit, or store any personal data</strong>. These apps include:
            </p>
            <ul>
              <li><strong>Civics Tutor 2025:</strong> All study progress, quiz results, and achievements 
              are saved locally on your device only. We do not collect any personal data, usage analytics, 
              or telemetry from this app. Your study activity is completely private and never leaves your device.</li>
            </ul>
            <p>
              For these apps, no data is collected, processed, or transmitted to our servers or any 
              third parties. All functionality operates entirely on your device.
            </p>
          </section>

          <section>
            <h2>Information We Collect</h2>
            
            <h3>Apps With Data Collection</h3>
            <p>
              The following information applies only to our applications and services that collect data 
              (such as apps with cloud sync, account features, or online services). This does NOT apply 
              to apps listed in the "Apps That Do Not Collect Data" section above.
            </p>
            
            <h3>Information You Provide</h3>
            <p>
              We may collect information that you provide directly when using our Services, including:
            </p>
            <ul>
              <li>Account information (email address, username, password)</li>
              <li>Profile information (name, preferences, settings)</li>
              <li>User-generated content (recipes, notes, lists, or other data you create within our apps)</li>
              <li>Communication data (support requests, feedback, or other correspondence)</li>
            </ul>

            <h3>Automatically Collected Information</h3>
            <p>
              When you access our Services, we may automatically collect certain information, including:
            </p>
            <ul>
              <li>Device information (device type, operating system, unique identifiers)</li>
              <li>Usage data (features used, time spent, interactions within the app)</li>
              <li>Log data (IP address, access times, browser type, crash reports)</li>
              <li>Location data (only if you grant permission, and only when necessary for app functionality)</li>
            </ul>
          </section>

          <section>
            <h2>How We Use Your Information</h2>
            <p>
              We use the information we collect to:
            </p>
            <ul>
              <li>Provide, maintain, and improve our Services</li>
              <li>Personalize your experience and deliver relevant content</li>
              <li>Process transactions and fulfill your requests</li>
              <li>Send you technical notices, updates, security alerts, and support messages</li>
              <li>Respond to your comments, questions, and customer service requests</li>
              <li>Monitor and analyze trends, usage, and activities in connection with our Services</li>
              <li>Detect, prevent, and address technical issues, fraud, or illegal activities</li>
              <li>Comply with legal obligations and protect our rights</li>
            </ul>
          </section>

          <section>
            <h2>Data Storage and Security</h2>
            <p>
              We implement appropriate technical and organizational measures to protect the security 
              of your personal information. However, please be aware that no method of transmission 
              over the internet or electronic storage is 100% secure.
            </p>
            <p>
              Some of our apps offer local storage options where your data is stored only on your device 
              and is not transmitted to our servers. For apps with cloud sync features, your data is 
              encrypted in transit and at rest.
            </p>
          </section>

          <section>
            <h2>Data Sharing and Disclosure</h2>
            <p>
              We do not sell, trade, or rent your personal information to third parties. We may share 
              your information only in the following circumstances:
            </p>
            <ul>
              <li><strong>With Your Consent:</strong> When you explicitly authorize us to share your information</li>
              <li><strong>Service Providers:</strong> With trusted third-party service providers who assist in operating our Services (e.g., cloud hosting, analytics, payment processing), subject to confidentiality obligations</li>
              <li><strong>Legal Requirements:</strong> When required by law, subpoena, or other legal process, or to protect our rights, property, or safety</li>
              <li><strong>Business Transfers:</strong> In connection with a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity</li>
            </ul>
          </section>

          <section>
            <h2>Third-Party Services</h2>
            <p>
              Our Services may contain links to third-party websites or integrate with third-party 
              services. We are not responsible for the privacy practices of these third parties. 
              We encourage you to review their privacy policies before providing any information.
            </p>
          </section>

          <section>
            <h2>Your Privacy Rights</h2>
            <p>
              Depending on your location, you may have certain rights regarding your personal information:
            </p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete information</li>
              <li><strong>Deletion:</strong> Request deletion of your personal information</li>
              <li><strong>Data Portability:</strong> Request a copy of your data in a structured, machine-readable format</li>
              <li><strong>Opt-Out:</strong> Opt out of certain data collection or processing activities</li>
            </ul>
            <p>
              To exercise these rights, please contact us at{' '}
              <a href="mailto:privacy@codetunezstudios.com">privacy@codetunezstudios.com</a>. 
              We will respond to your request in accordance with applicable law.
            </p>
          </section>

          <section>
            <h2>Children's Privacy</h2>
            <p>
              Our Services are not directed to children under the age of 13 (or the applicable age 
              in your jurisdiction). We do not knowingly collect personal information from children. 
              If we discover that a child has provided us with personal information, we will delete 
              it immediately. If you believe a child has provided us with personal information, 
              please contact us.
            </p>
          </section>

          <section>
            <h2>International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country 
              of residence. These countries may have data protection laws that differ from your jurisdiction. 
              We take appropriate measures to ensure that your information remains protected in accordance 
              with this Privacy Policy.
            </p>
          </section>

          <section>
            <h2>Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material 
              changes by posting the new Privacy Policy on this page and updating the "Last Updated" 
              date. Your continued use of our Services after any changes indicates your acceptance of 
              the updated Privacy Policy.
            </p>
          </section>

          <section>
            <h2>Contact Us</h2>
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our 
              privacy practices, please contact us at:
            </p>
            <div className="privacy-policy__contact">
              <p>
                <strong>CodeTunez Studios LLC</strong><br />
                Email: <a href="mailto:privacy@codetunezstudios.com">privacy@codetunezstudios.com</a><br />
                General Contact: <a href="mailto:contact@codetunezstudios.com">contact@codetunezstudios.com</a>
              </p>
            </div>
          </section>
        </article>
      </div>
    </main>
  );
};

export default PrivacyPolicyPage;
